import {
	AfterViewChecked, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener, Inject, OnDestroy, OnInit, ViewChild
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Subscription, interval } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { DestroyableComponent } from 'app/main/destroyable.component';
import { AssistantService } from '../assistant.service';
import { IAssistantAttachment, IAssistantContextResponse, IAssistantMessage, IAssistantSuggestion } from '../assistant.models';

/** Classe sul body a pannello aperto: da 1280px la pagina si restringe invece di finire sotto il pannello. */
const BODY_OPEN_CLASS = 'assistant-panel-open';
/** Oltre questa attesa si mostrano i secondi trascorsi (risposta lenta, non bloccata). */
const SHOW_ELAPSED_AFTER_SECONDS = 5;
/** "Sto pensando" all'inizio, poi "Sto lavorando alla risposta": l'attesa si legge come lavoro, non come blocco */
const WORKING_AFTER_SECONDS = 6;
const COMPOSER_MAX_HEIGHT = 160;
/** stessi limiti del server (AttachmentTextExtractor, AssistantController::MAX_ATTACHMENTS): errore subito, senza upload */
export const ATTACHMENT_EXTENSIONS = ['txt', 'md', 'csv', 'xlsx', 'xls', 'docx'];
const ATTACHMENT_MAX_BYTES = 5 * 1024 * 1024;
const MAX_ATTACHMENTS = 3;

/** file scelto nel campo di scrittura: in lettura sul server, pronto (attachment) o rifiutato (error) */
export interface IDraftAttachment {
	key: number;
	name: string;
	size: number;
	attachment?: IAssistantAttachment;
	error?: string;
}

/**
 * Pannello laterale dell'assistente: non modale, la pagina resta visibile e si aggiorna dopo le conferme.
 */
@Component({
	selector: 'assistant-panel',
	templateUrl: './assistant-panel.component.html',
	styleUrls: ['./assistant-panel.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false
})
export class AssistantPanelComponent extends DestroyableComponent implements OnInit, AfterViewChecked, OnDestroy{

	@ViewChild('scroller') scroller: ElementRef<HTMLElement>;
	@ViewChild('composer') composer: ElementRef<HTMLTextAreaElement>;
	@ViewChild('fileInput') fileInput: ElementRef<HTMLInputElement>;

	open = false;
	messages: IAssistantMessage[] = [];
	sending = false;
	elapsedSeconds = 0;
	context: IAssistantContextResponse | null = null;
	smart: { key: string | null; loading: boolean; items: IAssistantSuggestion[] | null } = { key: null, loading: false, items: null };
	draft = '';
	drafts: IDraftAttachment[] = [];
	/** file trascinato sopra il campo di scrittura */
	dropping = false;
	readonly attachmentAccept = ATTACHMENT_EXTENSIONS.map(extension => '.' + extension).join(',');
	private draftSequence = 0;
	private uploads = new Map<number, Subscription>();

	private shouldScroll = false;
	private shouldFocus = false;
	/** id del primo messaggio arrivato con l'ultima risposta: si scorre fin lì, non in fondo */
	private scrollToMessageId: number | null = null;
	private elapsedTimer: Subscription | null = null;

	constructor(
		public assistant: AssistantService,
		private translate: TranslateService,
		private changeDetector: ChangeDetectorRef,
		@Inject(DOCUMENT) private document: Document
	){
		super();
	}

	ngOnInit(): void{
		this.assistant.open.pipe(takeUntil(this._unsubscribeAll)).subscribe(open => {
			const changed = open !== this.open;
			this.open = open;
			this.document.body.classList.toggle(BODY_OPEN_CLASS, open);
			// la pagina cambia larghezza: grafici e tabelle che misurano la finestra si riallineano
			if(changed) setTimeout(() => window.dispatchEvent(new Event('resize')));
			if(open){
				this.shouldFocus = true;
				this.shouldScroll = true;
			}
		});
		this.assistant.messages.pipe(takeUntil(this._unsubscribeAll)).subscribe(messages => {
			if(messages.length > this.messages.length){
				const firstNew = messages[this.messages.length];
				// risposta dell'assistente (testo + cartellini): mostrarne l'inizio
				this.scrollToMessageId = firstNew.role === 'user' ? null : firstNew.id;
				this.shouldScroll = true;
			}
			this.messages = messages;
		});
		this.assistant.sending.pipe(takeUntil(this._unsubscribeAll)).subscribe(sending => {
			this.sending = sending;
			this.elapsedTimer?.unsubscribe();
			this.elapsedSeconds = 0;
			if(sending){
				this.shouldScroll = true;
				this.elapsedTimer = interval(1000).subscribe(seconds => this.elapsedSeconds = seconds + 1);
			}
		});
		this.assistant.serverContext.pipe(takeUntil(this._unsubscribeAll)).subscribe(context => this.context = context);
		// risposta HTTP asincrona: senza detectChanges la lista resterebbe quella a regole fino al prossimo evento
		this.assistant.smartSuggestions.pipe(takeUntil(this._unsubscribeAll)).subscribe(smart => {
			this.smart = smart;
			this.changeDetector.markForCheck();
		});
	}

	ngAfterViewChecked(): void{
		if(this.shouldScroll && this.scroller){
			const scroller = this.scroller.nativeElement;
			const target = this.scrollToMessageId !== null ? scroller.querySelector<HTMLElement>('[data-message-id="' + this.scrollToMessageId + '"]') : null;
			scroller.scrollTop = target ? target.offsetTop - 12 : scroller.scrollHeight;
			this.shouldScroll = false;
			this.scrollToMessageId = null;
		}
		if(this.shouldFocus && this.composer){
			this.composer.nativeElement.focus();
			this.shouldFocus = false;
		}
	}

	ngOnDestroy(): void{
		this.elapsedTimer?.unsubscribe();
		this.uploads.forEach(upload => upload.unsubscribe());
		this.document.body.classList.remove(BODY_OPEN_CLASS);
		super.ngOnDestroy();
	}

	@HostListener('document:keydown.escape')
	onEscape(): void{
		if(this.open) this.assistant.open.next(false);
	}

	/** consigli del modello per l'entità aperta se arrivati, altrimenti quelli a regole del context (subito, gratis) */
	get suggestions(): IAssistantSuggestion[]{
		if(this.messages.length || !this.context?.available) return [];
		if(this.smart.items && this.smart.key === this.entityKey) return this.smart.items;
		return this.context.suggestions || [];
	}

	/** frase d'apertura della sezione aperta (assistant/meta); prezzi se i metadati non sono ancora arrivati */
	get intro(): string{
		return this.assistant.areaIntro(this.context?.focus_area)
			?? 'Ecco cosa noto guardando i prezzi di oggi. Tocca un consiglio o scrivi tu: prima di modificare qualcosa ti mostro il riepilogo da confermare.';
	}

	get suggestionsLoading(): boolean{
		return this.smart.loading && this.smart.key === this.entityKey && !this.messages.length;
	}

	private get entityKey(): string | null{
		return this.assistant.smartSuggestionsKey(this.context?.entity ?? null);
	}

	suggestionIcon(suggestion: IAssistantSuggestion): string{
		return this.assistant.suggestionIcon(suggestion.kind);
	}

	get canSend(): boolean{
		return !this.sending && !this.uploading && (!!this.draft.trim() || this.readyAttachments.length > 0) && this.context?.available !== false;
	}

	get uploading(): boolean{
		return this.drafts.some(draft => !draft.attachment && !draft.error);
	}

	private get readyAttachments(): IAssistantAttachment[]{
		return this.drafts.filter(draft => draft.attachment).map(draft => draft.attachment!);
	}

	get canAttach(): boolean{
		return this.context?.available !== false && this.drafts.length < MAX_ATTACHMENTS;
	}

	openFilePicker(): void{
		if(this.canAttach) this.fileInput?.nativeElement.click();
	}

	onFilesPicked(input: HTMLInputElement): void{
		this.addFiles(Array.from(input.files || []));
		input.value = '';
	}

	onDragOver(event: DragEvent): void{
		if(!this.canAttach || !event.dataTransfer?.types.includes('Files')) return;
		event.preventDefault();
		event.dataTransfer.dropEffect = 'copy';
		this.dropping = true;
	}

	onDragLeave(event: DragEvent): void{
		const target = event.currentTarget as HTMLElement;
		if(!target.contains(event.relatedTarget as Node)) this.dropping = false;
	}

	onDrop(event: DragEvent): void{
		if(!this.dropping) return;
		event.preventDefault();
		this.dropping = false;
		this.addFiles(Array.from(event.dataTransfer?.files || []));
	}

	removeDraft(draft: IDraftAttachment): void{
		this.uploads.get(draft.key)?.unsubscribe();
		this.uploads.delete(draft.key);
		this.drafts = this.drafts.filter(item => item.key !== draft.key);
	}

	private addFiles(files: File[]): void{
		for (const file of files.slice(0, MAX_ATTACHMENTS - this.drafts.length)) {
			const draft: IDraftAttachment = { key: ++this.draftSequence, name: file.name, size: file.size };
			const extension = (file.name.split('.').pop() || '').toLowerCase();
			if(!ATTACHMENT_EXTENSIONS.includes(extension)){
				draft.error = this.translate.instant('Formato non supportato: usa txt, md, csv, Excel o Word');
			}else if(file.size > ATTACHMENT_MAX_BYTES){
				draft.error = this.translate.instant('File troppo grande: massimo 5 MB');
			}
			this.drafts = [...this.drafts, draft];
			if(draft.error) continue;
			this.uploads.set(draft.key, this.assistant.uploadAttachment(file).subscribe({
				next: attachment => this.patchDraft(draft.key, { attachment }),
				error: error => this.patchDraft(draft.key, { error: AssistantService.toError(error).message })
			}));
		}
	}

	private patchDraft(key: number, changes: Partial<IDraftAttachment>): void{
		this.uploads.delete(key);
		this.drafts = this.drafts.map(draft => draft.key === key ? { ...draft, ...changes } : draft);
	}

	formatSize(bytes: number): string{
		return bytes < 1024 ? bytes + ' B' : bytes < 1024 * 1024 ? Math.round(bytes / 1024) + ' KB' : (bytes / 1024 / 1024).toFixed(1).replace('.', ',') + ' MB';
	}

	attachmentIcon(name: string): string{
		const extension = (name.split('.').pop() || '').toLowerCase();
		return extension === 'xlsx' || extension === 'xls' || extension === 'csv' ? 'table_chart' : 'description';
	}

	/** errori temporanei: la stessa richiesta può riuscire (turno interrotto, timeout, rete, limite del provider) */
	retryable(code?: string): boolean{
		return ['rate_limited', 'llm_unavailable', 'llm_timeout', 'network', 'interrupted', 'timeout', 'turn_not_found'].includes(code || '');
	}

		get workingLabel(): string{
		return this.elapsedSeconds >= WORKING_AFTER_SECONDS ? 'Sto lavorando alla risposta' : 'Sto pensando';
	}

	get showElapsed(): boolean{
		return this.elapsedSeconds >= SHOW_ELAPSED_AFTER_SECONDS;
	}

	/** primo messaggio di una serie dell'assistente: porta l'avatar, i successivi no */
	startsAssistantRun(index: number): boolean{
		const message = this.messages[index];
		const previous = this.messages[index - 1];
		return message.role !== 'user' && (!previous || previous.role === 'user');
	}

	/** ultimo messaggio di una serie dello stesso autore: porta l'orario */
	endsRun(index: number): boolean{
		const next = this.messages[index + 1];
		const isUser = this.messages[index].role === 'user';
		return !next || (next.role === 'user') !== isUser;
	}

	onComposerKeydown(event: KeyboardEvent): void{
		if(event.key === 'Enter' && !event.shiftKey && !event.isComposing){
			event.preventDefault();
			this.submit();
		}
	}

	/** altezza che segue il testo (fallback per i browser senza field-sizing) */
	autosize(): void{
		const textarea = this.composer?.nativeElement;
		if(!textarea) return;
		textarea.style.height = 'auto';
		textarea.style.height = Math.min(textarea.scrollHeight, COMPOSER_MAX_HEIGHT) + 'px';
	}

	submitSuggestion(suggestion: IAssistantSuggestion): void{
		this.submit(suggestion.prompt);
	}

	submit(text?: string): void{
		const message = text ?? this.draft;
		// i consigli (text) partono senza allegati; dal campo di scrittura con quelli pronti
		const attachments = text === undefined ? this.readyAttachments : [];
		if((!message.trim() && !attachments.length) || this.sending || (text === undefined && this.uploading)) return;
		this.assistant.send(message, attachments);
		this.draft = '';
		if(text === undefined) this.drafts = [];
		this.shouldFocus = true;
		setTimeout(() => this.autosize());
	}
}
